import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Avatar,
  Row,
  Col,
  Space,
  Typography,
  Select,
  message
} from "antd";
import API from "Common/Client/endpoint";
import { PhoneOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useManagerProfile from "PlatForms/OrgLMS/Hooks/ManagerHooks/useManagerProfile";
import { Country, State, City } from "country-state-city";
import api from "Common/Client/Interceptor";
const { Text } = Typography;
const ManagerProfile = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const {
    profileData,
    loading,
    updating,
    imageUrl,
    fileList,
    beforeUpload,
    handleImageChange,
    updateProfile,
  } = useManagerProfile(form);
  // Country, State, City States
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);
  const [businessGroups, setBusinessGroups] = useState([]);
  useEffect(() => {
    const fetchBusinessGroups = async () => {
      try {
        const response = await api.get(API.businessGroup.getList);
        setBusinessGroups(response.data);
      } catch (error) {
        console.error("Failed to fetch business groups", error);
      }
    };

    fetchBusinessGroups();
  }, []);
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    const relatedStates = State.getStatesOfCountry(value);
    setStates(relatedStates);
    setCities([]);
    form.setFieldsValue({ state: undefined, city: undefined });
  };
  const handleStateChange = (value) => {
    setSelectedState(value);
    const relatedCities = City.getCitiesOfState(selectedCountry, value);
    setCities(relatedCities);
    form.setFieldsValue({ city: undefined });
  };
  const handleGroupChange = (value) => {
    setIsOtherSelected(value === "Other");
  };
  return (
    <Card loading={loading} title="Manager Profile" className="card-professional">
      <Form form={form} layout="vertical"
        onFinish={async (values) => {
          const success = await updateProfile(values, navigate);
          console.log("UpdateProfile returned:", success);

          // Always check backend status, even if success === false
          try {
            const res = await api.get("/Manager/ProfileUpdateStatus", {
              headers: { Authorization: `Bearer ${token}` },
            });
            console.log("Profile status:", res.data.isProfileCompleted);

            if (res.data.isProfileCompleted) {
              message.success("Profile completed!");
              navigate("/manager-dashboard", {
                state: { isProfileComplete: true },
                replace: true,
              });
            } else {
              message.warning("Profile saved but still incomplete.");
            }
          } catch (err) {
            console.error("Failed to check profile status", err);
          }
        }}
      >
        <Row gutter={[32, 16]} align="middle">
          <Col xs={24} md={6} style={{ textAlign: "center" }}>
            <Form.Item name="imageFile">
              <Upload
                name="imageFile"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={beforeUpload}
                onChange={handleImageChange}
                fileList={fileList}
                accept="image/*"
              >
                {imageUrl ? (
                  <Avatar src={imageUrl} size={110} style={{ border: "2px solid #eee" }} />
                ) : (
                  <Avatar
                    icon={<UserOutlined />}
                    size={110}
                    style={{ backgroundColor: "#f0f0f0" }}
                  />
                )}
              </Upload>
              <Text type="secondary">Click to change</Text>
            </Form.Item>
          </Col>

          <Col xs={24} md={18}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: "Please input your first name!" }]}
                >
                  <Input placeholder="John" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: "Please input your last name!" }]}
                >
                  <Input placeholder="Doe" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label="Email">
                  <Input value={profileData?.email || ""} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Business Group"
                  name="businessGroupId"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        // Only validate if "Other" is not selected
                        if (value !== "Other" && !value && !getFieldValue("otherGroupName")) {
                          return Promise.reject(
                            new Error("Please select a business group or specify your own!")
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <>
                    <Select
                      showSearch
                      placeholder="Select business group"
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={handleGroupChange}
                      options={[
                        ...businessGroups.map((group) => ({
                          label: group.name,
                          value: group.id,
                        })),
                        { label: "Other", value: "Other" },
                      ]}
                    />
                    {isOtherSelected && (
                      <Form.Item
                        name="otherGroupName"
                        style={{ marginTop: 16 }}
                        rules={[
                          {
                            required: true,
                            message: "Please specify your business group!",
                          },
                        ]}
                      >
                        <Input placeholder="Enter your Business Group Name" />
                      </Form.Item>
                    )}

                    {/* Status Text - Only show if not "Other" */}
                    {!isOtherSelected && profileData?.businessGroup?.status === 1 && (
                      <Text type="secondary">
                        Your business group <Text>{profileData?.businessGroup?.name}</Text> request
                        has been sent to the Admin and is still pending.
                      </Text>
                    )}
                    {!isOtherSelected && profileData?.businessGroup?.status === 2 && (
                      <Text strong>
                        Your business group is approved by the Admin:{" "}
                        <Text underline>{profileData?.businessGroup?.name}</Text>
                      </Text>
                    )}
                  </>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Manager Code"
                  name="managerCode"
                  rules={[{ required: true, message: "Please enter your Code!" }]}
                >
                  <Input placeholder="Enter your Code" disabled={!!profileData?.managerCode} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Phone Number"
                  name="phoneNo"
                  rules={[
                    { required: true, message: "Please enter your phone number!" },
                    {
                      pattern: /^\+?[0-9\s]*$/,
                      message: "Please enter only numbers and optional + prefix!",
                    },
                  ]}
                >
                  <Input
                    type="tel"
                    prefix={<PhoneOutlined />}
                    placeholder="+1234567890 or 0312345678"
                    onKeyPress={(e) => {
                      if (!/[0-9+]/.test(e.key) && e.key !== "Backspace") e.preventDefault();
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if ((value.match(/\+/g) || []).length > 1) {
                        e.target.value = value.replace(/\+/g, "");
                        if (value.startsWith("+")) e.target.value = "+" + e.target.value;
                      }
                    }}
                  />
                </Form.Item>
              </Col>

              {/* Country */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Country"
                  name="country"
                  rules={[{ required: true, message: "Please select your country!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your country"
                    optionFilterProp="children"
                    onChange={handleCountryChange}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={countries.map((country) => ({
                      label: country.name,
                      value: country.isoCode,
                    }))}
                  />
                </Form.Item>
              </Col>

              {/* State */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="State"
                  name="state"
                  rules={[
                    {
                      required: selectedCountry === "US",
                      message: "State is required when country is United States!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select your state"
                    optionFilterProp="children"
                    onChange={handleStateChange}
                    disabled={!selectedCountry}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={states.map((state) => ({
                      label: state.name,
                      value: state.isoCode,
                    }))}
                  />
                </Form.Item>
              </Col>

              {/* City */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="City"
                  name="city"
                  rules={[{ required: true, message: "Please select your city!" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select your city"
                    optionFilterProp="children"
                    disabled={!selectedState}
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={cities.map((city) => ({
                      label: city.name,
                      value: city.name,
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={updating}>
                      Update Profile
                    </Button>
                    <Button onClick={() => form.resetFields()} disabled={updating}>
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
export default ManagerProfile;
